import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import './TeacherFreeTier.css';

/**
 * TeacherLiteDashboard — landing dashboard for free-forever teacher tier.
 *
 * Per docs/FREE_TEACHER_TIER_SCOPE.md Day 3.
 *
 * Does NOT require school_license_id (unlike the full MorpheusInterface).
 * Shows: class info, active roster (or placeholder), mocked outcome preview,
 * referral CTA, upgrade-to-school CTA.
 */

const TeacherLiteDashboard = ({ user, onLogout }) => {
  const [roster, setRoster] = useState([]);
  const [classCode, setClassCode] = useState(null);
  const [className, setClassName] = useState('My class');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Day 4 endpoint — graceful fallback to empty state if not yet shipped
    const fetchClass = async () => {
      try {
        const response = await axios.get('/api/classroom/my-class');
        const data = response.data || {};
        if (data.students) setRoster(data.students);
        if (data.code) setClassCode(data.code);
        if (data.class_name) setClassName(data.class_name);
      } catch (err) {
        // No class created yet — show empty state
      } finally {
        setLoading(false);
      }
    };
    fetchClass();
  }, []);

  const goToOnboarding = () => navigate('/teacher/onboarding');
  const goToShare = () => navigate('/teacher/share');
  const goToUpgrade = () => navigate('/upgrade?source=teacher_dashboard&target=school');

  return (
    <div className="teacher-lite-page">
      {/* Top bar */}
      <div className="teacher-lite-topbar">
        <div className="teacher-lite-brand">
          <img src="/images/TAPPY-logo-black.png" alt="Tappy" className="teacher-lite-logo" />
          <span className="teacher-lite-tier-badge">Free</span>
        </div>
        <div className="teacher-lite-topbar-actions">
          <span className="teacher-lite-user">{user?.name || user?.username}</span>
          <button className="teacher-btn-ghost" onClick={onLogout}>Sign out</button>
        </div>
      </div>

      <div className="teacher-lite-main">
        {/* Welcome / primary panel */}
        <section className="teacher-lite-section">
          <h1 className="teacher-lite-heading">{className}</h1>
          <p className="teacher-lite-subhead">
            Free-forever tier · up to 30 students · unlimited Mira sessions
          </p>

          {classCode ? (
            <div className="teacher-code-display teacher-code-inline">
              <div className="teacher-code-label">Class code</div>
              <div className="teacher-code-value">{classCode}</div>
              {/* Scannable join QR — teacher projects the dashboard,
                  students scan. Generated client-side. */}
              <QRCodeSVG
                value={`${window.location.origin.replace('app.', '')}/class-join?code=${classCode}`}
                size={72}
                fgColor="#1b3f48"
                style={{ marginRight: 12, flexShrink: 0 }}
              />
              <button
                className="teacher-btn-secondary"
                onClick={() => navigator.clipboard?.writeText(classCode)}
              >
                Copy
              </button>
            </div>
          ) : (
            <div className="teacher-lite-cta-card">
              <p>You haven't created a class code yet. Generate one to start inviting students.</p>
              <button className="teacher-btn-primary" onClick={goToOnboarding}>
                Set up my class →
              </button>
            </div>
          )}
        </section>

        {/* Roster */}
        <section className="teacher-lite-section">
          <h2 className="teacher-lite-subheading">Students</h2>
          {loading ? (
            <div className="teacher-lite-empty">Loading...</div>
          ) : roster.length === 0 ? (
            <div className="teacher-lite-empty">
              <p>Your first student will appear here once they join with your class code.</p>
              <p className="teacher-lite-empty-hint">
                Students go to teachtappy.ai/class-join and enter the code + their first name.
                No email or password needed.
              </p>
            </div>
          ) : (
            <div className="teacher-lite-roster">
              {roster.map((student) => (
                <div key={student.id} className="teacher-lite-student-row">
                  <div className="teacher-lite-student-name">{student.first_name}</div>
                  <div className="teacher-lite-student-stats">
                    {student.sessions} sessions · mastery {Math.round((student.mastery || 0) * 100)}%
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Outcome preview — faded mock until real data exists */}
        <section className="teacher-lite-section teacher-lite-preview">
          <h2 className="teacher-lite-subheading">What you'll see after their first session</h2>
          <div className="teacher-lite-preview-card">
            <div className="teacher-lite-preview-row">
              <span>Mastery by topic</span>
              <span className="teacher-lite-preview-muted">Fractions · Photosynthesis · Pythagorean theorem</span>
            </div>
            <div className="teacher-lite-preview-row">
              <span>Misconceptions to address</span>
              <span className="teacher-lite-preview-muted">Teacher dashboard surfaces patterns across students</span>
            </div>
            <div className="teacher-lite-preview-row">
              <span>Minutes saved</span>
              <span className="teacher-lite-preview-muted">Estimated re-teach time spared by Mira</span>
            </div>
          </div>
          <p className="teacher-lite-preview-hint">
            This is a preview. Real data appears here as soon as a student completes a session.
          </p>
        </section>

        {/* Referral + upgrade */}
        <section className="teacher-lite-section teacher-lite-cta-grid">
          <div className="teacher-lite-cta-card">
            <h3 className="teacher-lite-cta-title">Share with a colleague</h3>
            <p>Teachers who refer get early access to new Tappy features. Free tier never expires.</p>
            <button className="teacher-btn-secondary" onClick={goToShare}>
              Invite a colleague
            </button>
          </div>

          <div className="teacher-lite-cta-card teacher-lite-cta-upgrade">
            <h3 className="teacher-lite-cta-title">Ready for your whole school?</h3>
            <p>
              Unlock Canvas / Schoology / Classroom integration, gradebook passback,
              roster sync, and unlimited students. Our team will work with your district on a DPA.
            </p>
            <button className="teacher-btn-primary" onClick={goToUpgrade}>
              See school plans
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default TeacherLiteDashboard;
